import type { EditorTab } from "../editor/reducers";
import { useParams } from "react-router";
import { useDocumentScreenSuspenseQuery } from "../client/generated";
import { assertTypeName } from "../client/guards";
import { Sidebar } from "../shell/Sidebar";
import { Scaffold } from "../shell/Scaffold";
import { NavbarMetadataTabButton } from "../editor/NavbarMetadataTabButton";
import { NavbarMetadataTabContent } from "../editor/NavbarMetadataTabContent";
import { PageThumbnail } from "../pages/PageThumbnail";
import { NavbarOverviewButton } from "../editor/NavbarOverviewButton";
import { NavbarPagesTabButton } from "../editor/NavbarPagesTabButton";
import { NavbarRegionsTabButton } from "../editor/NavbarRegionsTabButton";
import { SidebarDivider } from "../shell/SidebarDivider";
import { NavbarPreviousPageButton } from "../editor/NavbarPreviousPageButton";
import { NavbarNextPageButton } from "../editor/NavbarNextPageButton";
import { useState } from "react";
import { Navbar } from "../shell/Navbar";
import { ScrollArea } from "@acdc2/ui/components/scroll-area";
import { Headline } from "@acdc2/ui/components/access-typography";
import { NavbarExportTabButton } from "../editor/NavbarExportTabButton";
import { NavbarExportTabContent } from "../editor/NavbarExportTabContent";
import { PageIssuesBadge } from "../issues/PageIssuesBadge";

export function DocumentScreen() {
  const [tabValue, setTabValue] = useState<EditorTab>("metadata");
  const { documentId } = useParams<{ documentId: string }>();
  const { data } = useDocumentScreenSuspenseQuery({
    variables: { documentId: documentId! },
  });

  assertTypeName(data.node?.__typename === "Document");

  return (
    <Scaffold
      tabsValue={tabValue}
      onTabsValueChange={(newTabValue) => {
        setTabValue(newTabValue as EditorTab);
      }}
      navbar={
        <Navbar>
          <NavbarOverviewButton fragment={data.node} disabled />
          <NavbarPreviousPageButton />
          <NavbarNextPageButton />
          <SidebarDivider />
          <NavbarMetadataTabButton />
          <NavbarPagesTabButton disabled />
          <NavbarRegionsTabButton disabled />
          <NavbarExportTabButton />
        </Navbar>
      }
      sidebar={
        <Sidebar>
          <NavbarMetadataTabContent fragment={data.node} />
          <NavbarExportTabContent
            fragment={data.node}
            isActive={tabValue === "export"}
          />
        </Sidebar>
      }
    >
      <ScrollArea className="h-full bg-gray-100 dark:bg-gray-900">
        <div className="@container p-4 flex flex-col gap-4">
          <Headline asChild>
            <h1>{data.node.name}</h1>
          </Headline>
          <div className="grid gap-4 grid-cols-1 @md:grid-cols-2 @2xl:grid-cols-3 @4xl:grid-cols-4 @6xl:grid-cols-5">
            {data.node.pages.pages.map((page) => (
              <PageThumbnail key={page.id} fragment={page}>
                <PageIssuesBadge fragment={page} />
              </PageThumbnail>
            ))}
          </div>
        </div>
      </ScrollArea>
    </Scaffold>
  );
}
